import React, { useEffect } from 'react';
import { Platform } from '../../../types/Platforms';
import { ConnectedProfile } from '../../../types/customer';
import { VideoForPlatform } from '../types';
import { useSaveToPlatform } from '../hooks/useSaveToPlatform';
import { VideoPlatformSelector } from './VideoPlatformSelector';
import { PlatformDescriptionCard } from './PlatformDescriptionCard';
import { ThumbnailPicker } from './ThumbnailPicker';
import GenerateReviewLink from '../../../components/PostManager/GenerateReviewLink';
import { generateReviewLink, revokeReviewLink } from '../../../services/postService';

interface SaveToPlatformPanelProps {
  isOpen: boolean;
  onClose: () => void;
  video: VideoForPlatform;
  customerId: string;
  idToken: string;
  agentId: string;
  connectedProfiles?: ConnectedProfile[];
  /** Pre-selected platforms when opening from UGC modal Finalize section */
  initialSelectedPlatforms?: Platform[];
}

export const SaveToPlatformPanel: React.FC<SaveToPlatformPanelProps> = ({
  isOpen,
  onClose,
  video,
  customerId,
  idToken,
  agentId,
  connectedProfiles,
  initialSelectedPlatforms = [],
}) => {
  const {
    step,
    selectedPlatforms,
    descriptions,
    createdPostIds,
    reviewUrl,
    isGeneratingDescriptions,
    isSaving,
    isGeneratingReviewLink,
    error,
    thumbnailUrl,
    togglePlatform,
    generateDescriptions,
    updateDescription,
    setThumbnail,
    saveToReviewPosts,
    generateReview,
    reset,
  } = useSaveToPlatform({
    customerId,
    idToken,
    agentId,
    connectedProfiles,
    initialSelectedPlatforms,
  });

  // Reset when panel closes
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const connectedPlatformNames = connectedProfiles
    ?.flatMap((p) => p.platform ? [p.platform.toUpperCase()] : []) || [];

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
      <div className="bg-[#eae6da] w-full max-w-5xl h-[90vh] rounded-2xl overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="h-14 border-b border-[#e0dbcf] flex items-center justify-between px-6 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#2f4941] rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-[#2f4941]">Save to Platforms</h2>
            {step === 'success' && (
              <span className="px-2.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                Saved
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 text-[#2f4941]/60 hover:text-[#2f4941] hover:bg-[#e0dbcf] rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT — Video Preview */}
          <div className="w-2/5 border-r border-[#e0dbcf] bg-[#1a1a1a] flex flex-col items-center justify-center p-6 overflow-y-auto">
            <div className="w-full max-w-[260px] space-y-4">
              {/* Video player */}
              <div className="bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '9/16' }}>
                <video
                  src={video.videoUrl}
                  controls
                  className="w-full h-full object-contain"
                  playsInline
                />
              </div>

              {/* Thumbnail picker */}
              <ThumbnailPicker
                videoUrl={video.videoUrl}
                currentThumbnail={thumbnailUrl}
                onThumbnailChange={setThumbnail}
              />
            </div>
          </div>

          {/* RIGHT — Platform selection + descriptions */}
          <div className="w-3/5 flex flex-col overflow-hidden bg-white">
            <div className="flex-1 overflow-y-auto p-6">
              {/* Step: Select Platforms */}
              {(step === 'select_platforms' || step === 'generating_descriptions') && (
                <div className="space-y-6">
                  <VideoPlatformSelector
                    selectedPlatforms={selectedPlatforms}
                    onTogglePlatform={togglePlatform}
                    connectedPlatforms={connectedPlatformNames}
                    connectedProfiles={connectedProfiles}
                  />

                  {/* Generating indicator */}
                  {step === 'generating_descriptions' && (
                    <div className="flex flex-col items-center py-8 space-y-3">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2f4941]" />
                      <p className="text-sm text-[#2f4941] font-medium">
                        AI is generating descriptions...
                      </p>
                      <p className="text-xs text-[#2f4941]/60">
                        This may take a moment while your agent crafts platform-specific content
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Step: Edit Descriptions */}
              {step === 'edit_descriptions' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[#2f4941]">
                      Platform Descriptions
                    </h3>
                    <button
                      type="button"
                      onClick={() => reset()}
                      className="text-xs text-[#2f4941]/60 hover:text-[#2f4941] transition-colors"
                    >
                      Start over
                    </button>
                  </div>
                  {descriptions.map((desc) => (
                    <PlatformDescriptionCard
                      key={desc.platform}
                      description={desc}
                      onUpdate={(updates) => updateDescription(desc.platform, updates)}
                    />
                  ))}
                </div>
              )}

              {/* Step: Saving */}
              {step === 'saving' && (
                <div className="flex flex-col items-center py-12 space-y-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2f4941]" />
                  <p className="text-sm text-[#2f4941] font-medium">
                    Saving to review posts...
                  </p>
                </div>
              )}

              {/* Step: Success */}
              {step === 'success' && (
                <div className="space-y-6">
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-[#2f4941] mb-1">
                      Videos saved successfully!
                    </h3>
                    <p className="text-sm text-[#2f4941]/60">
                      {createdPostIds.length} post{createdPostIds.length !== 1 ? 's' : ''} created and ready for review
                    </p>
                  </div>

                  {/* Generate Review Link */}
                  <div className="border-t border-gray-200 pt-4">
                    <GenerateReviewLink
                      selectedPosts={createdPostIds.map((postId) => ({
                        customer_id: customerId,
                        post_id: postId,
                      }))}
                      onGenerate={async (posts) => generateReviewLink(posts, idToken)}
                      onRevoke={async (cid, linkId) => {
                        await revokeReviewLink(cid, linkId, idToken);
                      }}
                      onClear={() => {}}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Bottom action bar */}
            {step !== 'saving' && step !== 'success' && (
              <div className="p-4 border-t border-[#e0dbcf] bg-white flex-shrink-0">
                {step === 'select_platforms' && (
                  <button
                    type="button"
                    onClick={() => generateDescriptions(video)}
                    disabled={selectedPlatforms.length === 0 || isGeneratingDescriptions}
                    className="w-full px-6 py-3 bg-[#2f4941] text-white rounded-lg hover:bg-[#1e3128] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    Generate AI Descriptions
                  </button>
                )}

                {step === 'edit_descriptions' && (
                  <button
                    type="button"
                    onClick={() => saveToReviewPosts(video)}
                    disabled={descriptions.length === 0 || isSaving}
                    className="w-full px-6 py-3 bg-[#2f4941] text-white rounded-lg hover:bg-[#1e3128] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                      />
                    </svg>
                    Save to Review Posts
                  </button>
                )}
              </div>
            )}

            {/* Close button on success */}
            {step === 'success' && (
              <div className="p-4 border-t border-[#e0dbcf] bg-white flex-shrink-0">
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full px-6 py-3 bg-[#2f4941] text-white rounded-lg hover:bg-[#1e3128] transition-colors font-medium"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaveToPlatformPanel;
